import {BadgeProps} from './badges/Badge';
import {BadgeListProps} from './types';

export enum BadgeComponentType {
  BREAST_FEEDING_CAPABLE = 'breast-feeding-capable',
  CAMPAIGN_SALE = 'campaign',
  KIDS = 'kids',
  NEW = 'new',
  PACK = 'pack',
  PERSONALISED = 'personalised',
  PREMIUM = 'premium',
  SALE = 'sale',
  SIMILAR = 'similar',
  EXCLUSIVE = 'exclusive',
  SPECIAL_SIZES = 'specialSize',
  SPONSORED = 'sponsored',
  IN_BASKET = 'in-basket',
  SECOND_HAND = 'second-hand',
  EXA_DROP = 'exa_drop',
  DROP_EXCLUSIVE = 'drops_exclusive',
  SNEAKER_RELEASE_CALENDER = 'sneaker_release_calender',
  UPCYCLED = 'upcycled',
  UNIQUE = 'unique',
  BRAND_HAND_PICKED = 'brand_hand_picked',
  SECOND_LOVE = 'second_love',
}

export interface BadgeConfig {
  mustHave: boolean;
  isActive: boolean;
  props: Partial<BadgeProps>;
  componentType: BadgeComponentType;
}

export const getBadgesToDisplay = ({
  isNew,
  isPremium,
  isSponsored,
  specialSizes,
  isBreastFeedingCapable,
  pack,
  isKids,
  personalised,
  isSimilar,
  isExclusive,
  translations,
  sale: {saleText, campaignSaleText, campaignBackgroundColor},
  displayMode = 'all',
  isSecondHand,
  showSecondHand,
  isExaDrop,
  isSneakerReleaseCalenderDrop,
  isUpcycled,
  isUnique,
  brandHandPicked,
  isSecondLove,
  shouldCombineSaleAndCampaignBadges,
}: BadgeListProps) => {
  /**
   * Acceptance criteria for Badges display logic -> https://docs.google.com/document/d/1etEECLlRoYJWutZxziUlG44lImh5Zd7qYEnKIyipUos/edit
   * Gitlab Issue: https://gitlab.com/aboutyou/retail/aysa/aboutyou-mobile-web-react/-/issues/1135
   */
  const DEFAULT_MAX_BADGES_TO_DISPLAY = 2;

  const SORTED_BADGES: BadgeConfig[] = [
    {
      componentType: BadgeComponentType.SPONSORED,
      mustHave: true,
      props: {text: translations.sponsored},
      isActive: Boolean(displayMode !== 'excludeSponsored' && isSponsored),
    },
    {
      componentType: BadgeComponentType.SNEAKER_RELEASE_CALENDER,
      mustHave: false,
      props: {text: translations.sneakerReleaseCalender},
      isActive: Boolean(isSneakerReleaseCalenderDrop),
    },
    {
      componentType: BadgeComponentType.EXA_DROP,
      mustHave: false,
      props: {text: translations.exaDrop},
      isActive: Boolean(isExaDrop),
    },
    {
      componentType: BadgeComponentType.CAMPAIGN_SALE,
      mustHave: true,
      props: {
        text: campaignSaleText,
        backgroundColor: campaignBackgroundColor,
      },
      isActive: Boolean(campaignSaleText),
    },
    {
      componentType: BadgeComponentType.SALE,
      mustHave: true,
      props: {text: saleText},
      isActive: Boolean(saleText),
    },
    {
      componentType: BadgeComponentType.SPECIAL_SIZES,
      mustHave: true,
      props: {specialSizes, translations},
      isActive: Object.values(specialSizes).some(Boolean),
    },
    {
      componentType: BadgeComponentType.BREAST_FEEDING_CAPABLE,
      mustHave: true,
      props: {text: translations.breastFeedingCapable},
      isActive: Boolean(isBreastFeedingCapable),
    },
    {
      componentType: BadgeComponentType.KIDS,
      mustHave: true,
      props: {text: translations.kids},
      isActive: Boolean(isKids),
    },
    {
      componentType: BadgeComponentType.SECOND_HAND,
      mustHave: true,
      props: {text: translations.secondHand},
      isActive: Boolean(isSecondHand && showSecondHand),
    },
    {
      componentType: BadgeComponentType.PACK,
      mustHave: true,
      props: {text: pack},
      isActive: Boolean(pack),
    },
    {
      componentType: BadgeComponentType.SIMILAR,
      mustHave: true,
      props: {text: translations.similar},
      isActive: Boolean(isSimilar),
    },
    {
      componentType: BadgeComponentType.UPCYCLED,
      mustHave: true,
      props: {text: translations.upcycled},
      isActive: Boolean(isUpcycled),
    },
    {
      componentType: BadgeComponentType.UNIQUE,
      mustHave: true,
      props: {text: translations.unique},
      isActive: Boolean(isUnique),
    },
    {
      componentType: BadgeComponentType.BRAND_HAND_PICKED,
      mustHave: true,
      props: {text: brandHandPicked},
      isActive: Boolean(brandHandPicked),
    },
    {
      componentType: BadgeComponentType.SECOND_LOVE,
      mustHave: true,
      props: {text: translations.secondLove},
      isActive: Boolean(isSecondLove),
    },
    {
      componentType: BadgeComponentType.PERSONALISED,
      mustHave: false,
      props: {type: personalised, translations},
      isActive: Boolean(personalised),
    },
    {
      componentType: BadgeComponentType.NEW,
      mustHave: false,
      props: {text: translations.new},
      isActive: Boolean(isNew && !isSecondHand),
    },
    {
      componentType: BadgeComponentType.EXCLUSIVE,
      mustHave: false,
      props: {text: translations.exclusive},
      isActive: Boolean(isExclusive && !isSecondHand),
    },
    {
      componentType: BadgeComponentType.PREMIUM,
      mustHave: false,
      props: {text: translations.premium},
      isActive: Boolean(isPremium),
    },
  ].filter(({isActive}) => isActive);

  const saleTypes = [BadgeComponentType.SALE, BadgeComponentType.CAMPAIGN_SALE];

  if (shouldCombineSaleAndCampaignBadges && campaignSaleText && saleText) {
    const saleBadgeIndex = SORTED_BADGES.findIndex(
      badge =>
        badge.componentType === BadgeComponentType.SALE && badge.isActive,
    );

    SORTED_BADGES.splice(saleBadgeIndex, 1);

    const combinedCampaignAndSaleDiscount = getCombinedCampaignAndSaleDiscount({
      campaignReductionText: campaignSaleText,
      saleReductionText: saleText,
    });

    const campaignText = translations.deal?.(
      String(combinedCampaignAndSaleDiscount),
    );

    const campaignBadgeIndex = SORTED_BADGES.findIndex(
      badge =>
        badge.componentType === BadgeComponentType.CAMPAIGN_SALE &&
        badge.isActive,
    );

    SORTED_BADGES[campaignBadgeIndex] = {
      ...SORTED_BADGES[campaignBadgeIndex],
      props: {
        text: campaignText,
        backgroundColor: campaignBackgroundColor,
      } as any,
    };
  }

  if (shouldCombineSaleAndCampaignBadges && campaignSaleText && !saleText) {
    const campaignDiscount = getBadgeQuantity(campaignSaleText);

    const campaignText = translations.deal?.(String(campaignDiscount));

    const campaignBadgeIndex = SORTED_BADGES.findIndex(
      badge =>
        badge.componentType === BadgeComponentType.CAMPAIGN_SALE &&
        badge.isActive,
    );

    SORTED_BADGES[campaignBadgeIndex] = {
      ...SORTED_BADGES[campaignBadgeIndex],
      props: {
        text: campaignText,
        backgroundColor: campaignBackgroundColor,
      } as any,
    };
  }

  if (isExaDrop) {
    const exaDropBadgeIndex = SORTED_BADGES.findIndex(
      badge =>
        badge.componentType === BadgeComponentType.EXA_DROP && badge.isActive,
    );
    SORTED_BADGES.splice(exaDropBadgeIndex + 1, 0, {
      componentType: BadgeComponentType.DROP_EXCLUSIVE,
      mustHave: true,
      props: {text: translations.exclusive},
      isActive: true,
    });
  }

  const getFilteredBadges = () => {
    switch (displayMode) {
      case 'all':
        return SORTED_BADGES;
      case 'sustainableOnly':
        return SORTED_BADGES.filter(
          ({componentType}) => BadgeComponentType.SPONSORED === componentType,
        );
      case 'salesOnly':
        return SORTED_BADGES.filter(({componentType}) =>
          saleTypes.includes(componentType),
        );
      case 'excludeSales':
        return SORTED_BADGES.filter(
          ({componentType}) => !saleTypes.includes(componentType),
        );
      default:
        return SORTED_BADGES;
    }
  };

  const filteredBadges = getFilteredBadges();

  const numOfActiveMustHaveBadges = filteredBadges.filter(
    ({mustHave}) => mustHave,
  ).length;

  const maxBadgesToDisplay = Math.max(
    numOfActiveMustHaveBadges,
    DEFAULT_MAX_BADGES_TO_DISPLAY,
  );

  const displayedBadges = filteredBadges.slice(0, maxBadgesToDisplay);

  return prioritizeNewBadgeIfExists(displayedBadges);
};

/**
 * We need to show the `New` badge at the top when it is included in the list of badges to show.
 * This is needed because priority of showing a New Badge is different from where it is shown in the list, ie, always at the top.
 * But the priority to show New Badge has not changed from where it is in `SORTED_BADGES` array.
 */
const prioritizeNewBadgeIfExists = (badges: BadgeConfig[]) => {
  const newBadge = badges.find(
    badge => badge.componentType === BadgeComponentType.NEW,
  );
  return newBadge
    ? [
        newBadge,
        ...badges.filter(
          badge => badge.componentType !== BadgeComponentType.NEW,
        ),
      ]
    : badges;
};

export const getCombinedCampaignAndSaleDiscount = ({
  saleReductionText,
  campaignReductionText,
}: {
  saleReductionText: string;
  campaignReductionText: string;
}) => {
  const saleReduction = getBadgeQuantity(saleReductionText);
  const campaignReduction = getBadgeQuantity(campaignReductionText);
  const currentSaleReduction = 100 - saleReduction;
  const currentCampaignReduction = 100 - campaignReduction;
  const combinedDiscount = Math.floor(
    100 - (currentCampaignReduction * currentSaleReduction) / 100,
  );

  return combinedDiscount;
};

const getBadgeQuantity = (text: string) => {
  const digitOnlyRegExp = /\d+/;
  return Number(text.match(digitOnlyRegExp)?.[0] ?? 0);
};
